import React, { useEffect, useRef, useState } from 'react';
import { useNetworkQuality } from '../../../utils/useNetworkQuality';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

export default function NetworkIndicator({ intervalMs = 2000 }) {
  const { status } = useNetworkQuality({ intervalMs });

  const [dismissed, setDismissed] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(null);

  // ✅ Recovery banner
  const [showRecovered, setShowRecovered] = useState(false);

  // Track previous status
  const prevStatusRef = useRef(status);

  // --- Show banner immediately when offline/moderate
  useEffect(() => {
    const isBad = status === 'moderate' || status === 'offline';
    const now = Date.now();
    const inCooldown = cooldownUntil && now < cooldownUntil;

    // If bad network and not in cooldown, clear dismissal
    if (isBad && dismissed && !inCooldown) {
      setDismissed(false);
      setCooldownUntil(null);
    }
  }, [status, dismissed, cooldownUntil]);

  // ✅ FIXED: Show "Back online" ONLY after being offline (not moderate)
  useEffect(() => {
    const prev = prevStatusRef.current;
    const wasOffline = prev === 'offline';
    const isGood = status === 'good';

    // Show recovery ONLY when going from offline to good
    if (wasOffline && isGood) {
      setShowRecovered(true);
      setDismissed(false);
      setCooldownUntil(null);
    }

    prevStatusRef.current = status;
  }, [status]);

  // ✅ Auto-hide recovery banner after 3 seconds
  useEffect(() => {
    if (!showRecovered) return;

    const hideTimer = setTimeout(() => {
      setShowRecovered(false);
    }, 3000);

    return () => clearTimeout(hideTimer);
  }, [showRecovered]);

  // ✅ Back online banner
  if (showRecovered) {
    return (
      <div className="fixed top-3 right-16 z-[9999] shadow-lg rounded-lg px-4 py-2 text-white bg-[#0f537e]">
        <span className="text-sm font-medium">
          Connection Restored Successfully
        </span>
      </div>
    );
  }

  // Show banner if bad network and not dismissed
  const isBad = status === 'moderate' || status === 'offline';
  if (!isBad || dismissed) return null;

  const isOffline = status === 'offline';
  const bg = isOffline ? 'bg-red-600' : 'bg-[#0f537e]';
  const text = isOffline
    ? 'You are offline. Make sure you are connected to the internet.'
    : 'Network is unstable/slow. You may experience delays.';

  return (
    <div className={`fixed top-4 right-4 z-[9999] shadow-lg rounded-md pl-4 pr-10 py-2 text-white ${bg}`}>
      <span className="text-sm font-medium">{text}</span>
      <button
        type="button"
        aria-label="Dismiss network status"
        className="absolute top-1/2 -translate-y-1/2 right-2.5 text-white/90 hover:text-white focus:outline-none text-lg transition-colors"
        onClick={() => {
          setDismissed(true);
          setCooldownUntil(Date.now() + 15000);
        }}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
}
